import createApiClient from "./api";

const api = createApiClient();

export const createRootDocument = async (projectId) => {
  const response = await api.post(`/projects/${projectId}/documents/root`, {}, {
    headers: {},
  });
  return response.data;
};

export const createDocument = async (projectId, documentData) => {
  const response = await api.post(`/projects/${projectId}/documents`, documentData, {
    headers: {},
  });
  return response.data;
};

export const fetchDocuments = async (
  projectId,
  documentId = null,
  page = 1,
  pageSize = 10,
  filters = null,
  options = {}
) => {
  const url = documentId
    ? `/projects/${projectId}/documents/${documentId}`
    : `/projects/${projectId}/documents`;
  const response = await api.get(url, {
    params: {
      skip: (page - 1) * pageSize,
      limit: pageSize,
      filters: filters ? JSON.stringify(filters) : undefined,
    },
    headers: {},
    signal: options.signal,
  });

  const { data, total } = response.data;
  return { ...data, total };
};

export const uploadFilesToDocument = async (projectId, folderId, files) => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append("files", file);
  });

  const response = await api.post(
    `/projects/${projectId}/documents/${folderId}/files`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data;
};

const MAX_FILES_PER_UPLOAD = 10;
const MAX_BYTES_PER_UPLOAD = 200 * 1024 * 1024;

const splitIntoUploadBatches = (files) => {
  const batches = [];
  let batch = [];
  let batchBytes = 0;
  files.forEach((file) => {
    const size = file.size || 0;
    if (
      batch.length > 0 &&
      (batch.length >= MAX_FILES_PER_UPLOAD ||
        batchBytes + size > MAX_BYTES_PER_UPLOAD)
    ) {
      batches.push(batch);
      batch = [];
      batchBytes = 0;
    }
    batch.push(file);
    batchBytes += size;
  });
  if (batch.length > 0) batches.push(batch);
  return batches;
};

export const createFolderUploadState = () => ({
  folderIds: new Map(),
  uploadedFiles: new WeakSet(),
});

export const uploadFolderTreeToDocument = async (
  projectId,
  folderId,
  files,
  uploadState = createFolderUploadState(),
) => {
  const { folderIds, uploadedFiles } = uploadState;
  const filesByPath = new Map();
  files
    .filter((file) => !uploadedFiles.has(file))
    .forEach((file) => {
      const path = file.relativePath || "";
      if (!filesByPath.has(path)) filesByPath.set(path, []);
      filesByPath.get(path).push(file);
    });

  if (!folderIds.has("")) folderIds.set("", folderId);
  const ensureFolder = async (path) => {
    if (folderIds.has(path)) return folderIds.get(path);
    const separatorIndex = path.lastIndexOf("/");
    let parentId = await ensureFolder(
      separatorIndex === -1 ? "" : path.slice(0, separatorIndex),
    );
    if (parentId === "root") {
      parentId = (await createRootDocument(projectId)).id;
      folderIds.set("", parentId);
    }
    const created = await createDocument(projectId, {
      name: path.slice(separatorIndex + 1),
      parent_id: parentId,
      document_type: 1,
    });
    folderIds.set(path, created.id);
    return created.id;
  };

  for (const [path, folderFiles] of filesByPath) {
    const targetFolderId = await ensureFolder(path);
    for (const batch of splitIntoUploadBatches(folderFiles)) {
      await uploadFilesToDocument(projectId, targetFolderId, batch);
      batch.forEach((file) => uploadedFiles.add(file));
    }
  }
  return { reset_pagination: true };
};

export const deleteDocuments = async (projectId, documents) => {
  const response = await api.patch(`/projects/${projectId}/documents`, documents, {
    headers: {},
  });
  return response.data;
};

export const renameDocument = async (projectId, documentId, name) => {
  const response = await api.patch(
    `/projects/${projectId}/documents/${documentId}/rename`,
    { name },
    {
      headers: {},
    }
  );
  return response.data;
};

export const updateDocument = async (projectId, documentId, data) => {
  const response = await api.patch(
    `/projects/${projectId}/documents/${documentId}`,
    data,
    {
      headers: {},
    }
  );
  return response.data;
};

export const moveDocument = async (projectId, documentId, parentId) => {
  const response = await api.patch(
    `/projects/${projectId}/documents/${documentId}/move`,
    { parent_id: parentId },
    {
      headers: {},
    }
  );
  return response.data;
};

export const downloadDocument = async (projectId, documentId) => {
  const response = await api.get(`/projects/${projectId}/documents/${documentId}/download`, {
    responseType: "blob",
    headers: {},
  });
  return response;
};

export const fetchDocumentMergeSummary = async (projectId, documentId, filters = null) => {
  const response = await api.get(`/projects/${projectId}/documents/${documentId}/merge-pdf/summary`, {
    params: {
      filters: filters ? JSON.stringify(filters) : undefined,
    },
    headers: {},
  });
  return response.data;
};

export const mergeDocumentFolderPDFs = async (projectId, documentId, filters = null) => {
  const response = await api.get(`/projects/${projectId}/documents/${documentId}/merge-pdf`, {
    params: {
      filters: filters ? JSON.stringify(filters) : undefined,
    },
    responseType: "blob",
    headers: {},
  });
  return response;
};

export const notifyProjectUsersAboutDocument = async (projectId, documentId, userIds = null) => {
  const requestBody = {};
  if (userIds && Array.isArray(userIds) && userIds.length > 0) {
    requestBody.user_ids = userIds;
  }

  const response = await api.post(
    `/projects/${projectId}/documents/${documentId}/notify`,
    requestBody,
    { headers: {} }
  );
  return response.data;
};

export const fetchDocumentFolderShare = async (projectId, documentId) => {
  const response = await api.get(`/projects/${projectId}/documents/${documentId}/share`);
  return response.data;
};

export const shareDocumentFolder = async (projectId, documentId) => {
  const response = await api.post(`/projects/${projectId}/documents/${documentId}/share`);
  return response.data;
};

export const unshareDocumentFolder = async (projectId, documentId) => {
  const response = await api.delete(`/projects/${projectId}/documents/${documentId}/share`);
  return response.data;
};
